import React, { useState, useRef, useEffect, forwardRef, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  ListFilter,
  Tag,
  UserCircle,
  Check,
  ChevronLeft,
  CircleUserRound,
  Clock,
  Minus,
  ClockAlert,
  CircleDashed,
  TriangleDashed,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { HalfFilledTriangleIcon } from "@/assets/icons/half-filled-triangle-icon";
import { NoPriorityIcon, defaultPriorities } from "@/types/priorities";
import { ESTIMATE_POINTS, getTShirtSize } from "@/types/issues";
import { ShortcutHandler } from "../providers/ShortcutProvider";
import { useKanbanFilterStore } from "@/store/zustand/kanbanFilterStore";
import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import { columnStatuses } from "./CanvasKanban";
import { UserAvatar } from "../common/UserAvatar";

interface FilterOption {
  id: string;
  label: string;
  icon: React.ElementType;
  props?: any;
}

interface FilterMenuProps {
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

interface FilterMenuHandle {
  clearFilters: () => void;
  openMenu: () => void;
}

const filterOptions: FilterOption[] = [
  {
    id: "status",
    label: "Status",
    icon: CircleDashed,
    props: { color: "#606064" },
  },
  { id: "label", label: "Label", icon: Tag, props: { color: "#606064" } },
  {
    id: "assignee",
    label: "Assignee",
    icon: UserCircle,
    props: { color: "#606064" },
  },
  {
    id: "estimate",
    label: "Estimate",
    icon: TriangleDashed,
    props: { color: "#606064" },
  },
  {
    id: "priority",
    label: "Priority",
    icon: NoPriorityIcon,
    props: { color: "#606064" },
  },
  {
    id: "dueDate",
    label: "Due Date",
    icon: Clock,
    props: { color: "#606064" },
  },
];

const dueDateFilterOptions = [
  {
    id: "due-none",
    label: "No Due Date",
    icon: Clock,
    className: "text-white",
  },
  {
    id: "due-overdue",
    label: "Overdue",
    icon: ClockAlert,
    className: "text-red-500",
  },
  {
    id: "due-today",
    label: "Due Today",
    icon: Clock,
    className: "text-white",
  },
  {
    id: "due-this-week",
    label: "This Week",
    icon: Clock,
    className: "text-white",
  },

  {
    id: "due-next-week",
    label: "Next Week",
    icon: Clock,
    className: "text-white",
  },
  {
    id: "due-later",
    label: "Later",
    icon: Clock,
    className: "text-white",
  },
];

export const FilterMenu = forwardRef<FilterMenuHandle, FilterMenuProps>(
  ({ className, onOpenChange }, ref) => {
    // Get data directly instead of from props
    const project = useProjectWithUsers();
    const users = Array.isArray(project?.users) ? project.users : [];
    const labels = project?.labels || [];
    const statuses = columnStatuses;

    // Get filter state from Zustand store instead of local state
    const setFilter = useKanbanFilterStore((state) => state.setFilter);

    // Use specific selectors to avoid infinite loops
    const filterStatuses = useKanbanFilterStore((state) =>
      project?.id ? state.getFilter(project.id).statuses : []
    );
    const filterLabels = useKanbanFilterStore((state) =>
      project?.id ? state.getFilter(project.id).labels : []
    );
    const filterUserIds = useKanbanFilterStore((state) =>
      project?.id ? state.getFilter(project.id).userIds : []
    );
    const filterEstimates = useKanbanFilterStore((state) =>
      project?.id ? state.getFilter(project.id).estimates : []
    );
    const filterPriorities = useKanbanFilterStore((state) =>
      project?.id ? state.getFilter(project.id).priorities : []
    );
    const filterDueDates = useKanbanFilterStore((state) =>
      project?.id
        ? state.getFilter(project.id).dueDates
        : {
            none: false,
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            later: false,
          }
    );

    // Convert filter state to selectedFilters Set for the UI
    const selectedFilters = useMemo(() => {
      const filters = new Set<string>();

      // Add status filters
      filterStatuses?.forEach((status) => {
        filters.add(`status-${status}`);
      });

      // Add label filters
      filterLabels?.forEach((label) => {
        filters.add(`label-${label}`);
      });

      // Add user filters
      filterUserIds?.forEach((userId) => {
        filters.add(`assignee-${userId}`);
      });

      // Add estimate filters
      filterEstimates?.forEach((estimate) => {
        filters.add(`estimate-${estimate}`);
      });

      // Add priority filters
      filterPriorities?.forEach((priority) => {
        filters.add(`priority-${priority}`);
      });

      // Add due date filters
      if (filterDueDates) {
        if (filterDueDates.none) filters.add("due-none");
        if (filterDueDates.overdue) filters.add("due-overdue");
        if (filterDueDates.today) filters.add("due-today");
        if (filterDueDates.thisWeek) filters.add("due-this-week");
        if (filterDueDates.nextWeek) filters.add("due-next-week");
        if (filterDueDates.later) filters.add("due-later");
      }

      return filters;
    }, [
      filterStatuses,
      filterLabels,
      filterUserIds,
      filterEstimates,
      filterPriorities,
      filterDueDates,
    ]);

    const [searchQuery, setSearchQuery] = useState("");
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    // Expose methods through ref
    React.useImperativeHandle(ref, () => ({
      clearFilters: () => {
        if (!project) {
          return;
        }
        setFilter(project.id, {
          userIds: [],
          labels: [],
          search: "",
          statuses: [],
          estimates: [],
          priorities: [],
          dueDates: {
            none: false,
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            later: false,
          },
        });
      },
      openMenu: () => {
        setIsOpen(true);
      },
    }));

    // Helper function to update filters based on selectedFilters Set
    const updateFiltersFromSelected = (newSelectedFilters: Set<string>) => {
      const statuses: string[] = [];
      const labels: string[] = [];
      const userIds: string[] = [];
      const estimates: number[] = [];
      const priorities: number[] = [];
      const dueDates = {
        none: false,
        overdue: false,
        today: false,
        thisWeek: false,
        nextWeek: false,
        later: false,
      };

      newSelectedFilters.forEach((filter) => {
        if (filter.startsWith("status-")) {
          statuses.push(filter.replace("status-", ""));
        } else if (filter.startsWith("label-")) {
          labels.push(filter.replace("label-", ""));
        } else if (filter.startsWith("assignee-")) {
          userIds.push(filter.replace("assignee-", ""));
        } else if (filter.startsWith("estimate-")) {
          const value = filter.replace("estimate-", "");
          estimates.push(value === "none" ? 0 : parseInt(value, 10));
        } else if (filter.startsWith("priority-")) {
          const value = filter.replace("priority-", "");
          priorities.push(value === "none" ? 0 : parseInt(value, 10));
        } else if (filter === "due-none") {
          dueDates.none = true;
        } else if (filter === "due-overdue") {
          dueDates.overdue = true;
        } else if (filter === "due-today") {
          dueDates.today = true;
        } else if (filter === "due-this-week") {
          dueDates.thisWeek = true;
        } else if (filter === "due-next-week") {
          dueDates.nextWeek = true;
        } else if (filter === "due-later") {
          dueDates.later = true;
        }
      });

      if (!project) {
        return;
      }

      setFilter(project.id, {
        statuses,
        labels,
        userIds,
        estimates,
        priorities,
        dueDates,
      });
    };

    // Add scrollbar styles
    const scrollbarStyles = `
    .filter-menu-scroll::-webkit-scrollbar {
      width: 8px;
    }
    .filter-menu-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    .filter-menu-scroll::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.3);
      border-radius: 4px;
    }
    .filter-menu-scroll::-webkit-scrollbar-thumb:hover {
      background-color: rgba(255, 255, 255, 0.5);
    }
  `;

    useEffect(() => {
      if (isOpen) {
        // Use a small timeout to ensure the input is focused after the dropdown renders
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }, [isOpen, activeFilter]);

    // Add effect to maintain focus
    useEffect(() => {
      const input = inputRef.current;
      const menu = menuRef.current;
      if (!input || !isOpen) return;

      const handleFocusOut = (e: FocusEvent) => {
        // Prevent the blur and immediately restore focus
        e.preventDefault();
        if (isOpen) {
          // Use requestAnimationFrame to ensure focus is restored after any React updates
          requestAnimationFrame(() => {
            input.focus();
          });
        }
      };

      const handleMouseDown = (e: MouseEvent) => {
        // Don't prevent default on menu items or the input itself
        const target = e.target as HTMLElement;
        if (!target.closest('[role="menuitem"]') && target !== input) {
          e.preventDefault();
        }
        // Ensure input maintains focus even during mousedown
        requestAnimationFrame(() => {
          if (isOpen) {
            input.focus();
          }
        });
      };

      // Apply the mousedown handler to the entire dropdown content
      menu?.addEventListener("mousedown", handleMouseDown, { capture: true });
      input.addEventListener("blur", handleFocusOut);

      // Ensure focus is maintained after filtering
      const maintainFocus = () => {
        if (isOpen) {
          input.focus();
        }
      };

      // Add a small delay to ensure focus after React updates
      const timeoutId = setTimeout(maintainFocus, 0);

      return () => {
        clearTimeout(timeoutId);
        menu?.removeEventListener("mousedown", handleMouseDown, {
          capture: true,
        });
        input.removeEventListener("blur", handleFocusOut);
      };
    }, [isOpen, activeFilter, searchQuery]); // Add searchQuery as dependency

    // Separate effect for handling search query changes
    useEffect(() => {
      if (isOpen && inputRef.current) {
        // Ensure input maintains focus after search query updates
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }
    }, [searchQuery, isOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handleMouseMove = (e: MouseEvent) => {
        // Remove auto-focus behavior on mouse move
        if (menuRef.current?.contains(e.target as Node) && activeFilter) {
          return;
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      return () => document.removeEventListener("mousemove", handleMouseMove);
    }, [isOpen, activeFilter]);

    useEffect(() => {
      // Reset selection when filter changes
      setSelectedIndex(-1);
    }, [searchQuery, activeFilter]);

    const handleKeyNavigation = (e: React.KeyboardEvent) => {
      const menuItems = menuRef.current?.querySelectorAll('[role="menuitem"]');
      if (!menuItems?.length) return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();

        const direction = e.key === "ArrowDown" ? 1 : -1;
        const newIndex =
          selectedIndex === -1
            ? direction === 1
              ? 0
              : menuItems.length - 1
            : (selectedIndex + direction + menuItems.length) % menuItems.length;

        // Remove data-highlighted from all items
        menuItems.forEach((item) => {
          item.removeAttribute("data-highlighted");
        });

        // Add data-highlighted to the selected item
        menuItems[newIndex].setAttribute("data-highlighted", "true");

        setSelectedIndex(newIndex);
      } else if (e.key === "Enter" && selectedIndex !== -1) {
        e.preventDefault();
        (menuItems[selectedIndex] as HTMLElement).click();
      }
    };

    // Reset highlighted state when menu closes or filter changes
    useEffect(() => {
      if (!isOpen) {
        const menuItems =
          menuRef.current?.querySelectorAll('[role="menuitem"]');
        menuItems?.forEach((item) => {
          item.removeAttribute("data-highlighted");
        });
        setSelectedIndex(-1);
      }
    }, [isOpen, searchQuery, activeFilter]);

    const handleOpenChange = (open: boolean) => {
      // If we're in a submenu and trying to close, only allow it from the back button
      if (
        !open &&
        activeFilter &&
        !menuRef.current?.contains(document.activeElement)
      ) {
        return;
      }
      setIsOpen(open);
      if (!open) {
        setSearchQuery("");
        setActiveFilter(null);
      }
      onOpenChange?.(open);
    };

    const renderFilterContent = () => {
      if (!activeFilter) {
        const filteredOptions = filterOptions.filter((option) =>
          searchQuery
            ? option.label?.toLowerCase().includes(searchQuery.toLowerCase())
            : true
        );

        return (
          <div className="flex flex-col gap-1 px-1 body text-xs font-normal">
            <DropdownMenuItem
              className={cn(
                "flex items-center gap-2 cursor-pointer hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800",
                selectedFilters.size === 0 &&
                  "opacity-50 cursor-not-allowed pointer-events-none"
              )}
              onClick={(e) => {
                e.preventDefault();
                updateFiltersFromSelected(new Set());
              }}
              onSelect={(e) => e.preventDefault()}
            >
              <span className="flex-1 text-sm text-muted-foreground">
                Clear all filters
              </span>
            </DropdownMenuItem>
            {filteredOptions.map((option) => {
              const hasActiveFilters = Array.from(selectedFilters).some(
                (filter) => filter.startsWith(`${option.id}-`)
              );

              return (
                <DropdownMenuItem
                  key={option.id}
                  className="flex items-center gap-2 cursor-pointer p-1 hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveFilter(option.id);
                    setSearchQuery("");
                  }}
                  onSelect={(e) => e.preventDefault()}
                >
                  <div className="size-5 flex items-center justify-center">
                    <option.icon
                      className={cn("size-4 text-muted-foreground")}
                      {...option.props}
                    />
                  </div>
                  <span className="flex-1">{option.label}</span>
                  {hasActiveFilters && (
                    <div className="size-5 flex items-center justify-center">
                      <Check />
                    </div>
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        );
      }

      const option = filterOptions.find((opt) => opt.id === activeFilter);

      // Helper function to filter menu items based on search query
      const filterMenuItems = (items: JSX.Element[]) => {
        if (!searchQuery) return items;

        const findTextContent = (children: React.ReactNode): string => {
          if (typeof children === "string") return children;
          if (!children) return "";

          if (Array.isArray(children)) {
            return children.map((child) => findTextContent(child)).join(" ");
          }

          if (React.isValidElement(children)) {
            return findTextContent(children.props.children);
          }

          return "";
        };

        return items.filter((item) => {
          const text = findTextContent(item.props.children).toLowerCase();
          return text.includes(searchQuery.toLowerCase());
        });
      };

      const getMenuItems = () => {
        let items: JSX.Element[] = [];

        if (option?.id === "status") {
          items = statuses.map((status) => (
            <DropdownMenuCheckboxItem
              key={status.id}
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
              checked={selectedFilters.has(`status-${status.id}`)}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add(`status-${status.id}`);
                } else {
                  newSelectedFilters.delete(`status-${status.id}`);
                }
                updateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <div className="size-4 flex items-center justify-center">
                  {status.filterIcon ? <status.filterIcon /> : <status.icon />}
                </div>
                <span>{status.name}</span>
              </div>
            </DropdownMenuCheckboxItem>
          ));
        } else if (option?.id === "assignee") {
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none"
              checked={selectedFilters.has("assignee-none")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("assignee-none");
                } else {
                  newSelectedFilters.delete("assignee-none");
                }
                updateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <CircleUserRound className="size-5 text-secondary-n-600" />
                <span>Unassigned</span>
              </div>
            </DropdownMenuCheckboxItem>,
            ...users.map((user) => (
              <DropdownMenuCheckboxItem
                key={user.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
                checked={selectedFilters.has(`assignee-${user.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`assignee-${user.id}`);
                  } else {
                    newSelectedFilters.delete(`assignee-${user.id}`);
                  }
                  updateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <UserAvatar
                    userName={user.name || user.email}
                    userId={user.id}
                  />
                  <span className="truncate max-w-[140px]">
                    {user.name || user.email}
                  </span>
                </div>
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "priority") {
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
              checked={selectedFilters.has("priority-0")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("priority-0");
                } else {
                  newSelectedFilters.delete("priority-0");
                }
                updateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <Minus className="size-4 text-muted-foreground" />
                <span>No Priority</span>
              </div>
            </DropdownMenuCheckboxItem>,
            ...defaultPriorities.map((priority) => (
              <DropdownMenuCheckboxItem
                key={priority.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
                checked={selectedFilters.has(`priority-${priority.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`priority-${priority.id}`);
                  } else {
                    newSelectedFilters.delete(`priority-${priority.id}`);
                  }
                  updateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <priority.icon className="size-4 text-muted-foreground" />
                  <span>{priority.name}</span>
                </div>
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "label") {
          const sortedLabels = [...labels]
            .sort((a, b) => {
              return (a.label || "").localeCompare(b.label || "");
            })
            .filter((label) => {
              return label && label.label && label.label.length > 0;
            });
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
              checked={selectedFilters.has("label-none")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("label-none");
                } else {
                  newSelectedFilters.delete("label-none");
                }
                updateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <Tag className="size-4 text-muted-foreground" />
                <span>No label</span>
              </div>
            </DropdownMenuCheckboxItem>,
            ...sortedLabels.map((label) => (
              <DropdownMenuCheckboxItem
                key={label.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
                checked={selectedFilters.has(`label-${label.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`label-${label.id}`);
                  } else {
                    newSelectedFilters.delete(`label-${label.id}`);
                  }
                  updateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="size-2.5 rounded-[3px] flex-shrink-0"
                    style={{
                      backgroundColor: label.color?.value || "red",
                    }}
                  />
                  <span>{label.label}</span>
                </div>
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "estimate") {
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
              checked={selectedFilters.has("estimate-none")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("estimate-none");
                } else {
                  newSelectedFilters.delete("estimate-none");
                }
                updateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <TriangleDashed className="size-4 text-muted-foreground" />
                <span>No Estimate</span>
              </div>
            </DropdownMenuCheckboxItem>,
            ...ESTIMATE_POINTS.map((point) => (
              <DropdownMenuCheckboxItem
                key={point}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800"
                checked={selectedFilters.has(`estimate-${point}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`estimate-${point}`);
                  } else {
                    newSelectedFilters.delete(`estimate-${point}`);
                  }
                  updateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <HalfFilledTriangleIcon className="size-4 text-muted-foreground" />
                  <span>{getTShirtSize(point)}</span>
                </div>
              </DropdownMenuCheckboxItem>
            )),
          ];
        } else if (option?.id === "dueDate") {
          items = dueDateFilterOptions.map((option) => {
            return (
              <DropdownMenuCheckboxItem
                key={option.id}
                className={cn(
                  "flex items-center gap-2 select-none hover:bg-secondary-n-800 data-[highlighted]:bg-secondary-n-800",
                  option.className
                )}
                checked={selectedFilters.has(option.id)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(option.id);
                  } else {
                    newSelectedFilters.delete(option.id);
                  }
                  updateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div
                  className="flex items-center gap-2"
                  style={{ color: option.className }}
                >
                  <option.icon className="size-4" />
                  <span>{option.label}</span>
                </div>
              </DropdownMenuCheckboxItem>
            );
          });
        }

        return filterMenuItems(items);
      };

      return (
        <>
          <DropdownMenuItem
            className="flex items-center gap-2 cursor-pointer p-0"
            onClick={(e) => {
              e.preventDefault();
              setActiveFilter(null);
              setSearchQuery(""); // Clear search when going back
              inputRef.current?.focus();
            }}
            onSelect={(e) => e.preventDefault()}
          >
            <div className="size-5 flex items-center justify-center">
              <ChevronLeft className="size-4 text-muted-foreground" />
            </div>
            <span>{option?.label}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="-mx-1" />
          <div
            className="max-h-[256px] overflow-y-auto filter-menu-scroll"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
            }}
          >
            <style>{scrollbarStyles}</style>
            {getMenuItems()}
          </div>
        </>
      );
    };

    return (
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            className={cn(
              "flex items-center gap-1 px-1.5 ring-1 ring-inset ring-secondary-n-800 bg-background hover:bg-secondary-n-950 transition-colors relative data-[state=open]:bg-secondary-n-950",
              className
            )}
            size="sm"
            variant="secondary"
          >
            <div className="size-5 flex items-center justify-center">
              <ListFilter className="size-4" />
            </div>
            <p className="text-sm font-normal text-tertiary-n-500">Filter</p>
            {selectedFilters.size > 0 && (
              <div className="absolute -top-1 -right-1 size-2 rounded-full bg-orange-500" />
            )}
          </Button>
        </DropdownMenuTrigger>
        {isOpen && (
          <ShortcutHandler>
            <DropdownMenuContent
              ref={menuRef}
              align="end"
              className="w-56"
              onCloseAutoFocus={(e) => {
                e.preventDefault();
              }}
              onPointerDownOutside={(e) => {
                if (!menuRef.current?.contains(e.target as Node)) {
                  setIsOpen(false);
                }
              }}
            >
              <div className="px-2 py-2 border-secondary-n-800">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Filter"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  className="w-full bg-transparent text-sm border-0 outline-none focus:ring-0 p-0 placeholder:text-muted-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onBlur={(e) => {
                    if (menuRef.current?.contains(e.relatedTarget as Node)) {
                      e.preventDefault();
                      inputRef.current?.focus();
                    }
                  }}
                  onKeyDown={handleKeyNavigation}
                  autoFocus
                />
              </div>
              <DropdownMenuSeparator />
              {renderFilterContent()}
            </DropdownMenuContent>
          </ShortcutHandler>
        )}
      </DropdownMenu>
    );
  }
);

export default FilterMenu;
